import { FC, PropsWithChildren, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import Player from "@pages/Catalog/components/Player/Player";
import { CatalogPageContext } from "@pages/Catalog/context";

import { useAppSelector } from "@store/hooks";
import { getAccumulatedTracks, getCatalogueData, getTracksData, isLoadingCatalogue } from "@store/selectors/getCatalogueSelector";
import { getQuickFiltersSelector } from "@store/selectors/quickFilters";

import { ERoutes } from "@utils/constants";
import styled from "styled-components";

import AlbumList from "../../AlbumList";
import { QuickFilterCategory } from "../../QuickFilterCategory";
import { CatalogTab } from "../../utils";

// -- Styled components --

const TracksList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const AlbumsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
`;

const LoadMoreContainer = styled.div<{ $loading?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 8px;
  padding: 32px 0;
  gap: 12px;
  cursor: ${({ $loading }) => ($loading ? "default" : "pointer")};
  transition: background 0.2s ease;
  border-radius: 10px;

  &:hover {
    background: ${({ $loading }) => ($loading ? "transparent" : "rgba(255, 255, 255, 0.04)")};
  }

  &:active button {
    transform: ${({ $loading }) => ($loading ? "none" : "scale(0.97)")};
  }
`;

const LoadMoreButton = styled.button<{ $loading?: boolean }>`
  padding: 10px 32px;
  border-radius: 8px;
  min-width: 180px;
  border: none;
  background: rgba(156, 211, 253, 0.15);
  color: #9cd3fd;
  font-family: "Raleway", sans-serif;
  font-size: 0.8125rem;
  font-weight: 500;
  letter-spacing: 0.5px;
  cursor: inherit;
  transition: all 0.25s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  position: relative;
  overflow: hidden;
  pointer-events: none;

  ${({ $loading }) =>
    $loading &&
    `
    color: rgba(156, 211, 253, 0.5);

    &::after {
      content: "";
      position: absolute;
      right: 12px;
      width: 12px;
      height: 12px;
      border: 1.5px solid rgba(156, 211, 253, 0.3);
      border-top-color: #9cd3fd;
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }
  `}

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  &:disabled {
    opacity: 0.3;
    cursor: default;
    &:active {
      transform: none;
    }
  }
`;


// -- Component --

type TMainContentDesktop = {
  page: number;
  handleChange: (_event, value: number) => void;
  handleButtonPageChange: (e: boolean) => void;
} & PropsWithChildren;

export const CatalogTabDesktop: FC<TMainContentDesktop> = ({ page, handleChange: _handleChange, handleButtonPageChange }) => {
  const { state, t: _t, isSmartSearch, smartSearchHasMore, smartAlbums, handleSmartLoadMore } = useContext(CatalogPageContext);
  const { data: catalogueData, pageCount } = useAppSelector(getCatalogueData);
  const { data: _tracksData, pageCount: tracksPageCount } = useAppSelector(getTracksData);
  const accumulatedTracks = useAppSelector(getAccumulatedTracks);
  const isCatalogueLoading = useAppSelector(isLoadingCatalogue);
  const { quickFiltersCategories: quickCategories } = useAppSelector(getQuickFiltersSelector);

  const location = useLocation();

  // Coordinated reveal of the albums grid: preload every cover URL via Image()
  // promises, then fade the grid in as a unit when all are ready. Without this,
  // cards pop in one at a time as their <img> elements load, which the user
  // saw as "jumping in immediately, no smoothness". Errors don't block — a
  // single missing cover shouldn't gate the whole reveal.
  const albumsToShow = isSmartSearch && smartAlbums?.length ? smartAlbums : catalogueData;
  const [albumCoversReady, setAlbumCoversReady] = useState(false);
  useEffect(() => {
    setAlbumCoversReady(false);
    if (!albumsToShow?.length) return;
    // Preload the SAME variant the card displays (mediumCoverUrl — AlbumList).
    // Preloading smallCoverUrl while displaying medium meant the reveal gate
    // fired on the small covers, then the displayed medium covers loaded cold
    // afterwards (~5-6s blank). Matching them coordinates the reveal so the
    // grid fades in already-sharp.
    const urls = Array.from(
      new Set(
        albumsToShow
          .map((a: any) => a.cardCoverUrl || a.mediumCoverUrl || a.largeCoverUrl || a.smallCoverUrl)
          .filter((u: any): u is string => Boolean(u)),
      ),
    );
    if (!urls.length) {
      setAlbumCoversReady(true);
      return;
    }
    let cancelled = false;
    Promise.all(
      urls.map(
        (src) =>
          new Promise<void>((resolve) => {
            const img = new Image();
            img.onload = () => resolve();
            img.onerror = () => resolve();
            img.src = src;
          }),
      ),
    ).then(() => {
      if (cancelled) return;
      // Double-rAF — paints opacity:0 before flipping to 1, so the CSS
      // transition actually fires even when every image is already in the
      // browser cache (e.g. navigating away from /music and back).
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!cancelled) setAlbumCoversReady(true);
        });
      });
    });
    return () => {
      cancelled = true;
    };
  }, [albumsToShow]);

  // Coordinated reveal for the TRACKS tab (the default /music view). Each
  // <Player> row has its own TrackCover image — without this, they pop in
  // independently as their network/cache resolves. Same Promise.all + double-rAF
  // pattern as the album grid.
  const [trackCoversReady, setTrackCoversReady] = useState(false);
  // Track the previous list so a "load more" APPEND doesn't re-trigger the
  // fade. Without this the whole grid flashed opacity 0 → 1 on every load-more
  // (it read as the list "reloading").
  const prevTracksRef = useRef<{ len: number; firstId?: string }>({ len: 0 });
  useEffect(() => {
    const firstId = accumulatedTracks?.[0]?._id;
    const prev = prevTracksRef.current;
    // Append = list grew and the first item is unchanged (load-more). Keep the
    // existing rows visible; the new rows just appear as they render.
    const isAppend = prev.len > 0 && accumulatedTracks.length > prev.len && firstId === prev.firstId;
    prevTracksRef.current = { len: accumulatedTracks.length, firstId };
    if (isAppend) {
      setTrackCoversReady(true);
      return;
    }
    setTrackCoversReady(false);
    if (!accumulatedTracks?.length) return;
    const urls = Array.from(
      new Set(
        accumulatedTracks
          // Match the displayed variant — the <Player> TrackCover row thumbnail
          // is ~50px and renders smallCoverUrl, so preload that (not medium/card).
          .map((t: any) => t.smallCoverUrl || t.largeCoverUrl)
          .filter((u: any): u is string => Boolean(u)),
      ),
    );
    if (!urls.length) {
      setTrackCoversReady(true);
      return;
    }
    let cancelled = false;
    Promise.all(
      urls.map(
        (src) =>
          new Promise<void>((resolve) => {
            const img = new Image();
            img.onload = () => resolve();
            img.onerror = () => resolve();
            img.src = src;
          }),
      ),
    ).then(() => {
      if (cancelled) return;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!cancelled) setTrackCoversReady(true);
        });
      });
    });
    return () => {
      cancelled = true;
    };
  }, [accumulatedTracks]);

  const getPageCount = () => {
    if (state.catalogTab === CatalogTab.ALBUM) return pageCount;
    if (state.catalogTab === CatalogTab.TRACK || location.pathname.includes(ERoutes.QuickFilter))
      return tracksPageCount;
    return quickCategories.pageCount;
  };


  const hasMore = page < getPageCount();

  return (
    <>
      {/* TRACKS */}
      {(state.catalogTab === CatalogTab.TRACK || location.pathname.includes(ERoutes.QuickFilter)) && (
        <TracksList
          style={{
            opacity: trackCoversReady ? 1 : 0,
            transition: "opacity 700ms ease",
          }}
        >
          {accumulatedTracks.map((item, idx) => (
            <Player key={item._id} song={item} resultIndex={idx} />
          ))}
        </TracksList>
      )}

      {/* ALBUMS */}
      {state.catalogTab === CatalogTab.ALBUM && (
        <AlbumsGrid
          style={{
            opacity: albumCoversReady ? 1 : 0,
            transition: "opacity 700ms ease",
          }}
        >
          {albumsToShow.map((item) => (
            <AlbumList key={item._id} album={item} isCollapsed={state.isFiltersOpen} />
          ))}
        </AlbumsGrid>
      )}

      {/* QUICK FILTERS */}
      {state.catalogTab === CatalogTab.QUICK_FILTERS &&
        !location.pathname.includes(ERoutes.QuickFilter) &&
        quickCategories.data
          .filter((item) => item.quickFilters.length)
          .map((item) => <QuickFilterCategory key={item._id} {...item} />)}

      {/* LOAD MORE */}
      {isSmartSearch && smartSearchHasMore && state.catalogTab === CatalogTab.TRACK && !isCatalogueLoading && accumulatedTracks.length > 0 && (
        <LoadMoreContainer onClick={handleSmartLoadMore}>
          <LoadMoreButton type="button">
            Show More Tracks
          </LoadMoreButton>
        </LoadMoreContainer>
      )}
      {!isSmartSearch &&
        (state.catalogTab !== CatalogTab.QUICK_FILTERS || location.pathname.includes(ERoutes.QuickFilter)) &&
        getPageCount() > 1 && (
          <LoadMoreContainer $loading={isCatalogueLoading} onClick={() => !isCatalogueLoading && hasMore && handleButtonPageChange(true)}>
            <LoadMoreButton
              type="button"
              disabled={!hasMore}
              $loading={isCatalogueLoading}
              data-loading={isCatalogueLoading || undefined}
            >
              {state.catalogTab === CatalogTab.ALBUM ? "Show More Albums" : "Show More Tracks"}
            </LoadMoreButton>
          </LoadMoreContainer>
        )}
    </>
  );
};
